import { Action } from "@/api-schema-enums";
import { useAbility } from "@/casl-provider";
import { useNavigationItem } from "@/common/hooks/use-navigation";
import { useUrlEnumState } from "@/common/hooks/use-url-enum-state";
import { DeviceAware } from "@/components/device-aware/device-aware";
import { CenteredLayout, CenteredLayoutVariant } from "@/components/layouts/centered-layout";
import { MultiToggle } from "@/components/multi-toggle/multi-toggle";
import { userStore } from "@/store/user-store";
import { NotFoundView } from "@/views/common/notfound.view";
import { HandCoinsIcon, ScaleIcon } from "lucide-react";
import { type ReactElement } from "react";
import { useTranslation } from "react-i18next";
import { FabElectronicSignatureTemplateConfigurations } from "./_components/fab-electronic-signature-template-configurations";
import { FabValuationAdditions } from "./_components/fab-valuation-additions";
import { FabValuationPriceAdjustments } from "./_components/fab-valuation-price-adjustments";
import { FabValuationWarranty } from "./_components/fab-valuation-warranty";
import { FabSettingsTab, getAllowedFabSettingsTabs } from "./_helpers/fab-settings-tabs.helpers";

export function FabSettingsView(): ReactElement {
  const { t } = useTranslation("fab");
  const ability = useAbility();
  const user = userStore((state) => state.user);
  const isBuyInAvailable = useNavigationItem("fab-buy-in") !== undefined;
  const allowedTabs = getAllowedFabSettingsTabs({
    roles: user?.roles,
    canManageBuyInSettings: ability.can(Action.Manage, "ElectronicSignatureTemplateConfiguration"),
    isBuyInAvailable,
  });
  const [activeTab, setActiveTab] = useUrlEnumState<FabSettingsTab>(
    "tab",
    allowedTabs,
    allowedTabs[0] ?? FabSettingsTab.Valuation,
  );
  const tabOptions = [
    { label: t("ValuationSettingsTab"), icon: ScaleIcon, value: FabSettingsTab.Valuation },
    { label: t("BuyInSettingsTab"), icon: HandCoinsIcon, value: FabSettingsTab.BuyIn },
  ].filter((option) => allowedTabs.includes(option.value));

  if (!allowedTabs.includes(activeTab)) {
    return <NotFoundView />;
  }

  return (
    <CenteredLayout
      variant={CenteredLayoutVariant.Default}
      title={t("ValuationSettings")}
      subTitle={t("ValuationSettingsDescription")}
    >
      {tabOptions.length > 1 ? (
        <DeviceAware
          mobile={
            <div className="relative flex w-full flex-row justify-center">
              <MultiToggle
                aria-label={t("FabSettingsCategory")}
                className="w-full max-w-100"
                options={tabOptions}
                onChange={setActiveTab}
                value={activeTab}
              />
            </div>
          }
          fallback={
            <div className="relative flex flex-row justify-center">
              <MultiToggle
                aria-label={t("FabSettingsCategory")}
                options={tabOptions}
                onChange={setActiveTab}
                value={activeTab}
              />
            </div>
          }
        />
      ) : null}

      {activeTab === FabSettingsTab.Valuation ? (
        <>
          <FabValuationPriceAdjustments />
          <FabValuationAdditions />
          <FabValuationWarranty />
        </>
      ) : (
        <FabElectronicSignatureTemplateConfigurations />
      )}
    </CenteredLayout>
  );
}
